import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EntitiesList from '@features/entities/entities-list/ui/EntitiesList/EntitiesList';
import EvEntityRowForm from '@features/ev-editor/EvEntityRowForm';
import { useGroupSelectionContext } from '@context/groupSelectionContext';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import { useToastContext } from '@context/toastContext';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectSelectedFeatures } from '@redux/selectors/selectors';
import GroupSelectionSetsCreateAction from './GroupSelectionSetsCreateAction';
import GroupSelectionSetsMergeAction from './GroupSelectionSetsMergeAction';

const GroupSelectionSetsList = ({ onSave }) => {
    const [selectedId, setSelectedId] = useState('');
    const [selectedForMergeIds, setSelectedForMergeIds] = useState<string[]>([]);
    const {
        loading,
        availableSelectionSets,
        getGroupSelectionSets,
        getGroupSelectionSetById,
        deleteGroupSelectionSet,
    } = useGroupSelectionContext();
    const selectedFeatures = useTypedSelector(selectSelectedFeatures);
    const { groupselectionId } = useParams();
    const { addToast } = useToastContext();
    const { t } = useTranslation();

    useEffect(() => {
        const eventHandler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setSelectedId('');
                setSelectedForMergeIds([]);
            }
        };

        document.addEventListener('keyup', eventHandler);

        return () => {
            document.removeEventListener('keyup', eventHandler);
        };
    }, [setSelectedId]);

    useEffect(() => {
        if (groupselectionId) {
            void (async function () {
                const selectedSet = availableSelectionSets.find(
                    ({ id }) => id === groupselectionId
                );
                if (selectedSet) {
                    setSelectedId(selectedSet.id);
                    await getGroupSelectionSetById(groupselectionId);
                }
            })();
        } else {
            setSelectedId('');
        }
    }, [groupselectionId, availableSelectionSets, getGroupSelectionSetById]);

    const onRowClick = useCallback(
        async (entity: any) => {
            const { id } = entity;
            setSelectedId(id);

            const resp = await getGroupSelectionSetById(id);
            if (isErrorResponse(resp)) {
                addToast(resp.error_description, 'error');
                return;
            }
        },
        [getGroupSelectionSetById]
    );

    const deleteHandler = useCallback(
        async (id: string) => {
            const resp = await deleteGroupSelectionSet(id);
            if (isErrorResponse(resp)) {
                try {
                    const parsedErr =
                        typeof resp.error === 'string'
                            ? safelyParseJson(resp.error)?.error
                            : resp.error.error;
                    addToast(parsedErr, 'error');
                } catch (e) {
                    console.log('Replan. Error parsing error response', resp, e);
                }
            } else {
                addToast(t('common.save-successful'), 'success');
            }
        },
        [deleteGroupSelectionSet]
    );

    const onRowSelection = useCallback((id: string) => {
        setSelectedForMergeIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(selectedId => selectedId !== id);
            }

            if (prev.length >= 2) {
                return [prev[1], id];
            }

            return [...prev, id];
        });
    }, []);

    return (
        <div className="group-selection-sets">
            <p>{t('road-network.group-selection-selection-sets-title')}</p>
            <div className="group-selection-sets-controls">
                <GroupSelectionSetsCreateAction
                    selectedFeatures={selectedFeatures}
                    onSave={onSave}
                />
                <GroupSelectionSetsMergeAction
                    selectedForMergeIds={selectedForMergeIds}
                    onSave={onSave}
                    onMergeSuccess={() => setSelectedForMergeIds([])}
                />
            </div>
            <div className="group-selection-sets-list">
                {availableSelectionSets.length ? (
                    <EntitiesList
                        loading={loading}
                        entities={availableSelectionSets}
                        onRowClick={onRowClick}
                        form={EvEntityRowForm}
                        deleteFn={deleteHandler}
                        loadDataFn={getGroupSelectionSets}
                        selectedId={selectedId}
                        showRowCheckbox
                        onRowSelection={onRowSelection}
                        selectedRowsIds={selectedForMergeIds}
                    />
                ) : (
                    <div className="group-selection-no-sets">
                        {t('road-network.group-selection-no-sets')}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupSelectionSetsList;
